import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { formatRelativeTime } from "../../utils";
import { highlightHtmlContent } from "../../utils";
import { CardSkeleton } from "../Skeleton";
import { NoteCard } from "../Note";
import TaskCard from "./TaskCard";
import EditJobModal from "./EditJobModal";
import TaskModal from "../../../Modals/TaskModal";
import SlideshowModal from "./SlideshowModal";
import "./StackedView.css";
import { useTaskInfiniteScroll } from "../../hooks/useTaskInfiniteScroll";
import ProjectStack from "./ProjectStack";
import { StackedViewLoading } from "./StackedViewLoading ";
import CollapsedStackView from "./CollapsedStackView";
import toast from "react-hot-toast";
import Modal from "../../../Modals/Modal";
import JobPermissionManagement from "../../../JobPermission/JobPermissionManagement";

// ========== Constants ==========
const TASK_STATUSES = {
  TODO: { id: 1, name: "To Do", icon: "fa-circle", color: "#6c757d", order: 1 },
  IN_PROGRESS: {
    id: 2,
    name: "In Progress",
    icon: "fa-spinner",
    color: "#fd7e14",
    order: 2,
  },
  ARCHIVED: {
    id: 3,
    name: "Archived",
    icon: "fa-archive",
    color: "#6c757d",
    order: 5,
  },
  BLOCKED: {
    id: 4,
    name: "Blocked",
    icon: "fa-times",
    color: "#dc3545",
    order: 3,
  },
  DONE: {
    id: 5,
    name: "Done",
    icon: "fa-check-circle",
    color: "#28a745",
    order: 4,
  },
};

const ALL_TASKS_TAB = {
  id: 0,
  name: "All Tasks",
  icon: "fa-tasks",
  color: "#3498db",
  order: 0,
};

// Style constants (moved outside component)
const styles = {
  initialLoadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "60px 40px",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    textAlign: "center",
    color: "#3498db",
    fontSize: "14px",
    fontWeight: 500,
  },
  loadingMoreIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "20px",
    color: "#3498db",
    fontSize: "14px",
    marginTop: "16px",
    borderTop: "1px solid #e9ecef",
  },
  noMoreNotesMessage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "16px",
    color: "#6c757d",
    fontSize: "12px",
    marginTop: "16px",
    borderTop: "1px solid #e9ecef",
  },
  expandedContainer: {
    width: "100%",
    marginBottom: "20px",
  },
  expandedHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 16px",
    backgroundColor: "#f8f9fa",
    borderBottom: "2px solid #e9ecef",
    borderRadius: "8px 8px 0 0",
    minHeight: "40px",
    position: "sticky",
    top: "0",
    zIndex: "100",
  },
  tasksContainer: {
    marginTop: "20px",
    padding: "0 20px 20px 20px",
  },
  tabsRowContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px",
    gap: "16px",
    marginBottom: "5px",
    borderBottom: "1px solid #e9ecef",
    position: "sticky",
    top: "20px",
    backgroundColor: "white",
    zIndex: "8",
    flexWrap: "wrap",
  },
  tabsContainer: {
    display: "flex",
    gap: "4px",
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    scrollbarWidth: "thin",
    msOverflowStyle: "auto",
    WebkitOverflowScrolling: "touch",
    paddingBottom: "5px",
    flex: 1,
  },
  tabButton: {
    padding: "5px 10px",
    background: "transparent",
    border: "none",
    borderRadius: "8px 8px 0 0",
    fontSize: "13px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    position: "relative",
    marginBottom: "-1px",
  },
  tabCount: {
    marginLeft: "3px",
    padding: "1px 3px",
    borderRadius: "12px",
    fontSize: "10px",
    fontWeight: 600,
    minWidth: "20px",
    textAlign: "center",
  },
  assigneeFilterInline: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "0 0 0 16px",
    borderLeft: "1px solid #e9ecef",
    flexShrink: 0,
  },
  assigneeFilterLabelInline: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#6c757d",
    whiteSpace: "nowrap",
  },
  assigneeListInline: {
    display: "flex",
    alignItems: "center",
  },
  assigneeAvatarsInline: {
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  assigneeRingInline: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    border: "2px solid #e9ecef",
    backgroundColor: "#6c757d",
    color: "white",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "11px",
    fontWeight: 600,
    position: "relative",
    marginLeft: "0",
    flexShrink: 0,
  },
  assigneeRingInitialsInline: {
    fontWeight: 600,
    fontSize: "11px",
  },
  assigneeActiveDotInline: {
    position: "absolute",
    bottom: "-2px",
    right: "-2px",
    width: "9px",
    height: "9px",
    backgroundColor: "#28a745",
    borderRadius: "50%",
    border: "2px solid white",
  },
  assigneeClearRingInline: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    backgroundColor: "#dc3545",
    color: "white",
    border: "2px solid #e9ecef",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "9px",
    marginRight: "2px",
    zIndex: 20,
    flexShrink: 0,
  },
  assigneeMoreBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    backgroundColor: "#e9ecef",
    color: "#6c757d",
    fontSize: "11px",
    fontWeight: 600,
    marginLeft: "-4px",
    border: "2px solid white",
    cursor: "default",
  },
  assigneeLoading: {
    padding: "4px 8px",
    color: "#6c757d",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  tasksGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: "16px",
  },
  taskWrapper: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  loadingState: {
    padding: "40px",
    textAlign: "center",
    color: "#666",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  spinner: {
    fontSize: "24px",
    color: "#3498db",
    marginBottom: "10px",
    display: "block",
  },
  emptyState: {
    padding: "40px",
    textAlign: "center",
    color: "#999",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  emptyTabState: {
    padding: "60px 40px",
    textAlign: "center",
    color: "#999",
    backgroundColor: "#fafafa",
    borderRadius: "8px",
    border: "1px dashed #dee2e6",
  },
  emptyIcon: {
    fontSize: "32px",
    marginBottom: "10px",
    opacity: 0.5,
    display: "block",
  },
  createButton: {
    marginTop: "10px",
    padding: "8px 16px",
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    transition: "background-color 0.2s ease",
  },
  expandedTaskSection: {
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e9ecef",
  },
  taskNotesSection: {
    marginTop: "16px",
  },
  notesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "16px",
  },
  noNotesMessage: {
    padding: "30px",
    textAlign: "center",
    color: "#999",
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "13px",
  },
  clearFilterButton: {
    marginTop: "12px",
    padding: "6px 12px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    transition: "background-color 0.2s ease",
  },
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const StackedView = React.memo(
  ({
    stackedJobs,
    loadingStackedJobs,
    expandedStacks,
    toggleStackExpansion,
    hasActiveFilters,
    searchTerm,
    openAiDialogForJob,
    renderStackedImageIcon,
    displayNotes,
    selectedRow,
    handleRowClick,
    handleRowDoubleClick,
    handleAddFromRow,
    handleEdit,
    handleDelete,
    handleViewAttachments,
    handleReplyToNote,
    handlePriorityClick,
    handlePriorityMouseEnter,
    handlePriorityMouseLeave,
    handleNoteTextMouseEnter,
    handleNoteTextMouseLeave,
    handleLinkedNoteClick,
    handleLinkedNoteMouseEnter,
    handleLinkedNoteMouseLeave,
    isNoteReply,
    getReplyNoteId,
    isOriginalNoteExists,
    userStatusMap,
    loadingUsers,
    getPriorityValue,
    manuallyUpdatedPriorities,
    shouldShowNotePopup,
    isStackedViewLoading,
    isFilteringStacked,
    jobsToDisplay,
    onSaveTask,
    userId,
    tasksByJob = {},
    loadingTasksByJob = {},
    expandedTasks = {},
    toggleTaskExpansion,
    notesByTask = {},
    loadingNotesByTask = {},
    fetchTasksForJob,
    fetchNotesForTask,
    onTaskEdit,
    onTaskDelete,
    onTaskStatusChange,
    onTaskClick,
    isClearingFilter,
    refreshTasksForJob,
    onFilterByProject,
    onFilterByWorkspace,
    refreshNotes,
    highlightedTaskId = null,
    clearHighlightedTask = () => {},
    loadMoreTaskNotes,
    taskPagination,
    workspaceRole,
    // NEW PROPS FROM NOTESTAB (lifted state)
    expandedProjects,
    setExpandedProjects,
    activeTabByJob,
    handleTabChange,
    assigneeFilters,
    handleAssigneeFilter,
    clearAssigneeFilter,
  }) => {
    // ========== State (only local UI, not persistent) ==========
    const [localExpandedStacks, setLocalExpandedStacks] = useState({});
    const [editJobModalOpen, setEditJobModalOpen] = useState(false);
    const [jobToEdit, setJobToEdit] = useState(null);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [selectedJobForTask, setSelectedJobForTask] = useState(null);
    const [isSavingTask, setIsSavingTask] = useState(false);
    const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState(null);
    const [selectedJobForTaskEdit, setSelectedJobForTaskEdit] = useState(null);
    const [isUpdatingTask, setIsUpdatingTask] = useState(false);
    const [slideshowOpen, setSlideshowOpen] = useState(false);
    const [slideshowNotes, setSlideshowNotes] = useState([]);
    const [slideshowJobName, setSlideshowJobName] = useState("");
    const [slideshowStartIndex, setSlideshowStartIndex] = useState(0);
    const [loadingSlideshowJob, setLoadingSlideshowJob] = useState(null);
    const [highlightedTask, setHighlightedTask] = useState(null);
    const [jobUsers, setJobUsers] = useState({});
    const [loadingUsersByJob, setLoadingUsersByJob] = useState({});
    const [jobUsersTask, setJobUsersTask] = useState({});
    const [loadingUsersByJobTask, setLoadingUsersByJobTask] = useState({});
    const [jobPermissionModalOpen, setJobPermissionModalOpen] = useState(false);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedJobForPermission, setSelectedJobForPermission] = useState(null);

    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    // ========== Memoized grouped jobs ==========
    const { projects, ungroupedJobs } = useMemo(() => {
      const projectsMap = {};
      const ungrouped = [];
      if (!stackedJobs) return { projects: [], ungroupedJobs: [] };
      stackedJobs.forEach((job) => {
        if (!job) return;
        const projectId = job.projectId;
        if (projectId && projectId !== null && projectId !== undefined && projectId !== "") {
          if (!projectsMap[projectId]) {
            projectsMap[projectId] = {
              projectId: job.projectId,
              projectName: job.projectName || "Unnamed Project",
              projectDescription: job.projectDescription,
              workspaceId: job.workspaceId,
              workspaceName: job.workspaceName,
              jobs: [],
            };
          }
          projectsMap[projectId].jobs.push(job);
        } else {
          ungrouped.push(job);
        }
      });
      let projectsArray = Object.values(projectsMap);
// Sort projects by the most recent note timestamp among their jobs
projectsArray.sort((a, b) => {
  const aLatest = Math.max(...a.jobs.map(job => new Date(job.latestTimeStamp || 0).getTime()));
  const bLatest = Math.max(...b.jobs.map(job => new Date(job.latestTimeStamp || 0).getTime()));
  return bLatest - aLatest;
});
return { projects: projectsArray, ungroupedJobs: ungrouped };
    }, [stackedJobs]);

    const isAnyProjectExpanded = useMemo(
      () => Object.values(expandedProjects).some((v) => v),
      [expandedProjects]
    );

    // ========== Callbacks ==========
    const fetchJobPermissionUsers = useCallback(
      async (jobId) => {
        if (jobUsers[jobId] || loadingUsersByJob[jobId]) return;
        setLoadingUsersByJob((prev) => ({ ...prev, [jobId]: true }));
        try {
          const response = await fetch(
            `${apiUrl}/api/Permissions/permissions-by-job/${jobId}`
          );
          if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
          const data = await response.json();
          const users = data.permissions || [];
          const colors = [
            "#3498db", "#e74c3c", "#2ecc71", "#f39c12",
            "#9b59b6", "#1abc9c", "#e67e22", "#34495e",
          ];
          const formattedUsers = users.map((user, index) => ({
            id: user.userId,
            name: user.fullName || user.userName,
            username: user.userName,
            initials: getInitials(user.fullName || user.userName),
            color: colors[index % colors.length],
            ...user,
          }));
          setJobUsers((prev) => ({ ...prev, [jobId]: formattedUsers }));
        } catch (error) {
          console.error(`Error fetching users for job ${jobId}:`, error);
          toast.error(`Failed to load users for job`);
        } finally {
          setLoadingUsersByJob((prev) => ({ ...prev, [jobId]: false }));
        }
      },
      [apiUrl, jobUsers, loadingUsersByJob]
    );

    const fetchJobTaskUsers = useCallback(
      async (jobId) => {
        if (jobUsersTask[jobId] || loadingUsersByJobTask[jobId]) return;
        setLoadingUsersByJobTask((prev) => ({ ...prev, [jobId]: true }));
        try {
          const response = await fetch(
            `${apiUrl}/api/Permissions/users-by-job-with-task/${jobId}`
          );
          if (!response.ok) throw new Error(`Failed to fetch task users: ${response.status}`);
          const data = await response.json();
          const users = data.users || [];
          const colors = [
            "#3498db", "#e74c3c", "#2ecc71", "#f39c12",
            "#9b59b6", "#1abc9c", "#e67e22", "#34495e",
          ];
          const formattedUsers = users.map((user, index) => ({
            id: user.userId,
            name: user.fullName || user.userName,
            username: user.userName,
            initials: getInitials(user.fullName || user.userName),
            color: colors[index % colors.length],
            accessLevel: user.accessLevel,
            assignedAt: user.assignedAt,
            ...user,
          }));
          setJobUsersTask((prev) => ({ ...prev, [jobId]: formattedUsers }));
        } catch (error) {
          console.error(`Error fetching task users for job ${jobId}:`, error);
          toast.error(`Failed to load task users for job`);
        } finally {
          setLoadingUsersByJobTask((prev) => ({ ...prev, [jobId]: false }));
        }
      },
      [apiUrl, jobUsersTask, loadingUsersByJobTask]
    );

    const handleCreateTask = useCallback((job) => {
      setSelectedJobForTask({
        id: job.jobId || job.id || job.jobID,
        name: job.jobName,
      });
      setTaskModalOpen(true);
    }, []);

    const handleSaveTask = useCallback(
      async (taskData) => {
        setIsSavingTask(true);
        try {
          if (onSaveTask) {
            await refreshNotes?.();
            await onSaveTask(taskData);
            toast.success(`Task "${taskData.taskName}" created successfully!`);
            setTaskModalOpen(false);
            if (selectedJobForTask) {
              await refreshTasksForJob?.(selectedJobForTask.id);
              await fetchTasksForJob?.(selectedJobForTask.id);
            }
          }
        } catch (error) {
          console.error("Failed to save task:", error);
          toast.error(`Failed to create task: ${error.message}`);
        } finally {
          setIsSavingTask(false);
        }
      },
      [onSaveTask, refreshNotes, selectedJobForTask, refreshTasksForJob, fetchTasksForJob]
    );

    const handleTaskEdit = useCallback(
      (task) => {
        let jobForTask = null;
        for (const [jobId, tasks] of Object.entries(tasksByJob)) {
          if (tasks.some((t) => t.id === task.id)) {
            const job = stackedJobs?.find((j) => j && j.jobId === parseInt(jobId));
            if (job) jobForTask = job;
            break;
          }
        }
        if (!jobForTask && task.jobId) {
          jobForTask = stackedJobs?.find(
            (j) => j && (j.jobId === task.jobId || j.id === task.jobId || j.jobID === task.jobId)
          );
        }
        if (jobForTask) {
          setTaskToEdit(task);
          setSelectedJobForTaskEdit({
            id: jobForTask.jobId || jobForTask.id || jobForTask.jobID,
            name: jobForTask.jobName,
          });
          setEditTaskModalOpen(true);
        } else {
          console.error("Could not find job for task:", task);
          toast.error("Could not find the job for this task");
        }
      },
      [tasksByJob, stackedJobs]
    );

    const handleTaskUpdate = useCallback(
      async (updatedTask) => {
        setIsUpdatingTask(true);
        try {
          if (onTaskEdit) {
            await onTaskEdit(updatedTask);
            toast.success("Task updated successfully");
            setEditTaskModalOpen(false);
            setTaskToEdit(null);
            setSelectedJobForTaskEdit(null);
            if (selectedJobForTaskEdit && fetchTasksForJob) {
              await fetchTasksForJob(selectedJobForTaskEdit.id);
            }
          }
        } catch (error) {
          console.error("Failed to update task:", error);
          toast.error(`Failed to update task: ${error.message}`);
        } finally {
          setIsUpdatingTask(false);
        }
      },
      [onTaskEdit, selectedJobForTaskEdit, fetchTasksForJob]
    );

    const handleOpenSlideshow = useCallback(
      async (job, startIndex = 0) => {
        const jobId = job.jobId || job.id || job.jobID;
        setLoadingSlideshowJob(jobId);
        try {
          if (job.notes && job.notes.length > 0) {
            setSlideshowNotes(job.notes);
            setSlideshowJobName(job.jobName);
            setSlideshowStartIndex(startIndex);
            setSlideshowOpen(true);
          } else {
            const toastId = toast.loading(`Loading notes for ${job.jobName}...`);
            const params = new URLSearchParams({
              pageNumber: "1",
              pageSize: "1000",
              jobId,
              userId,
            });
            const response = await fetch(
              `${apiUrl}/api/SiteNote/GetSiteNotesByJobId?${params.toString()}`
            );
            if (!response.ok) throw new Error(`Failed to fetch notes: ${response.status}`);
            const data = await response.json();
            const formattedNotes = (data.siteNotes || []).map((n) => ({
              ...n,
              id: n.id,
              userName: n.userName || n.UserName || "Unknown",
              documentCount: n.documentCount || 0,
              timeStamp: n.timeStamp || n.date,
              date: n.date || n.createdDate,
              workspace: n.workspace || "",
              project: n.project || "",
              job: n.job || job.jobName,
              note: n.note || n.content,
              jobId: n.jobId || jobId,
            }));
            job.notes = formattedNotes;
            setSlideshowNotes(formattedNotes);
            setSlideshowJobName(job.jobName);
            setSlideshowStartIndex(startIndex);
            setSlideshowOpen(true);
            toast.dismiss(toastId);
            toast.success(`Loaded ${formattedNotes.length} notes for slideshow`);
          }
        } catch (error) {
          console.error("Error fetching notes for slideshow:", error);
          toast.error(`Failed to load notes: ${error.message}`);
        } finally {
          setLoadingSlideshowJob(null);
        }
      },
      [apiUrl, userId]
    );

    const handleTaskCardClick = useCallback(
      (task) => {
        if (onTaskClick) {
          onTaskClick(task);
        } else if (toggleTaskExpansion) {
          toggleTaskExpansion(task.id, task.jobId);
        }
      },
      [onTaskClick, toggleTaskExpansion]
    );

    const handleDenyUserClick = useCallback((job, workspaceId, userId) => {
      if (!workspaceId) {
        toast.error("Cannot manage permissions: Workspace ID not found");
        return;
      }
      if (!userId) {
        toast.error("Cannot manage permissions: User ID not found");
        return;
      }
      setSelectedWorkspaceId(workspaceId);
      setSelectedUserId(userId);
      setSelectedJobForPermission(job);
      setJobPermissionModalOpen(true);
    }, []);

    // ========== Effects ==========
    useEffect(() => {
      const handleHighlight = (event) => {
        const { taskId, jobId } = event.detail;
        setHighlightedTask({ id: taskId, jobId });
        setTimeout(() => {
          setHighlightedTask(null);
          clearHighlightedTask?.();
        }, 2000);
      };
      window.addEventListener("highlightTask", handleHighlight);
      return () => window.removeEventListener("highlightTask", handleHighlight);
    }, [clearHighlightedTask]);

    useEffect(() => {
      if (highlightedTaskId) {
        setHighlightedTask({ id: highlightedTaskId });
        setTimeout(() => {
          setHighlightedTask(null);
          clearHighlightedTask?.();
        }, 2000);
      }
    }, [highlightedTaskId, clearHighlightedTask]);

    // ========== Early returns ==========
    if (isStackedViewLoading || isClearingFilter) {
      return <StackedViewLoading />;
    }

    if (loadingStackedJobs) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px", flexWrap: "wrap", width: "100%" }}>
            {[...Array(4)].map((_, i) => (
              <div
                key={`row1-${i}`}
                className="job-stack-skeleton"
                style={{
                  width: "300px",
                  height: "280px",
                  flexShrink: 0,
                  background: "#ffffff",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                }}
              >
                {/* skeleton content – keep as in original */}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px", flexWrap: "wrap", width: "100%" }}>
            {[...Array(4)].map((_, i) => (
              <div key={`row2-${i}`} className="job-stack-skeleton" style={{ /* same */ }}>
                {/* skeleton content */}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (!stackedJobs || stackedJobs.length === 0) {
      return (
        <div className="empty-state">
          <i className="fas fa-layer-group" style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }} />
          <h3>
            {hasActiveFilters ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", padding: "20px", color: "#1976d2" }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: "24px" }} />
                <span style={{ fontSize: "16px", fontWeight: "500", color: "#495057" }}>Loading jobs...</span>
              </div>
            ) : (
              "No stacked jobs available"
            )}
          </h3>
        </div>
      );
    }

    // ========== Main render ==========
    return (
      <div className="stacked-notes-horizontal">
        <div className="stacked-container">
          {projects.map((project) => {
            const isProjectExpanded = expandedProjects[project.projectId];
            if (isAnyProjectExpanded && !isProjectExpanded) return null;
            return (
              <ProjectStack
                key={`project-${project.projectId}`}
                project={project}
                stackedJobs={stackedJobs}
                expandedProjects={expandedProjects}
                toggleProjectExpansion={() => {
                  setExpandedProjects((prev) => ({
                    ...prev,
                    [project.projectId]: !prev[project.projectId],
                  }));
                }}
                expandedStacks={expandedStacks}
                toggleStackExpansion={toggleStackExpansion}
                searchTerm={searchTerm}
                hasActiveFilters={hasActiveFilters}
                openAiDialogForJob={openAiDialogForJob}
                renderStackedImageIcon={renderStackedImageIcon}
                selectedRow={selectedRow}
                TASK_STATUSES={TASK_STATUSES}
                ALL_TASKS_TAB={ALL_TASKS_TAB}
                handleRowClick={handleRowClick}
                handleRowDoubleClick={handleRowDoubleClick}
                handleAddFromRow={handleAddFromRow}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleViewAttachments={handleViewAttachments}
                handleReplyToNote={handleReplyToNote}
                handlePriorityClick={handlePriorityClick}
                handlePriorityMouseEnter={handlePriorityMouseEnter}
                handlePriorityMouseLeave={handlePriorityMouseLeave}
                handleNoteTextMouseEnter={handleNoteTextMouseEnter}
                handleNoteTextMouseLeave={handleNoteTextMouseLeave}
                handleLinkedNoteClick={handleLinkedNoteClick}
                handleLinkedNoteMouseEnter={handleLinkedNoteMouseEnter}
                handleLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
                isNoteReply={isNoteReply}
                getReplyNoteId={getReplyNoteId}
                isOriginalNoteExists={isOriginalNoteExists}
                userStatusMap={userStatusMap}
                loadingUsers={loadingUsers}
                getPriorityValue={getPriorityValue}
                manuallyUpdatedPriorities={manuallyUpdatedPriorities}
                shouldShowNotePopup={shouldShowNotePopup}
                userId={userId}
                tasksByJob={tasksByJob}
                jobUsersTask={jobUsersTask}
                loadingUsersByJobTask={loadingUsersByJobTask}
                loadingTasksByJob={loadingTasksByJob}
                expandedTasks={expandedTasks}
                toggleTaskExpansion={toggleTaskExpansion}
                notesByTask={notesByTask}
                loadingNotesByTask={loadingNotesByTask}
                fetchTasksForJob={fetchTasksForJob}
                fetchNotesForTask={fetchNotesForTask}
                onTaskEdit={handleTaskEdit}
                onTaskDelete={onTaskDelete}
                onTaskStatusChange={onTaskStatusChange}
                onTaskClick={handleTaskCardClick}
                refreshTasksForJob={refreshTasksForJob}
                onFilterByProject={onFilterByProject}
                onFilterByWorkspace={onFilterByWorkspace}
                refreshNotes={refreshNotes}
                highlightedTaskId={highlightedTaskId}
                clearHighlightedTask={clearHighlightedTask}
                loadMoreTaskNotes={loadMoreTaskNotes}
                taskPagination={taskPagination}
                onOpenEdit={(job) => {
                  setJobToEdit(job);
                  setEditJobModalOpen(true);
                }}
                onCreateTask={handleCreateTask}
                onOpenSlideshow={handleOpenSlideshow}
                activeTabByJob={activeTabByJob}
                onTabChange={handleTabChange}
                assigneeFilters={assigneeFilters}
                jobUsers={jobUsers}
                loadingUsersByJob={loadingUsersByJob}
                onAssigneeFilter={handleAssigneeFilter}
                clearAssigneeFilter={clearAssigneeFilter}
                fetchJobPermissionUsers={fetchJobPermissionUsers}
                workspaceRole={workspaceRole}
                onDenyUserClick={handleDenyUserClick}
                fetchJobTaskUsers={fetchJobTaskUsers}
                
              />
            );
          })}

          {/* Modals */}
          {editJobModalOpen && jobToEdit && (
            <EditJobModal
              isOpen={editJobModalOpen}
              onClose={() => {
                setEditJobModalOpen(false);
                setJobToEdit(null);
              }}
              jobId={jobToEdit.jobId || jobToEdit.id || jobToEdit.jobID}
              workspaceId={
                jobToEdit.workspaceId ||
                jobToEdit.workspace ||
                jobToEdit.defWorkId ||
                null
              }
              projectId={jobToEdit.projectId || jobToEdit.project?.id || null}
              projectName={jobToEdit.projectName || jobToEdit.project?.name || ""}
              userId={jobToEdit.userId}
              onJobUpdated={() => {
                setEditJobModalOpen(false);
                setJobToEdit(null);
              }}
            />
          )}

          {taskModalOpen && selectedJobForTask && (
            <TaskModal
              isOpen={taskModalOpen}
              onClose={() => {
                setTaskModalOpen(false);
                setSelectedJobForTask(null);
              }}
              jobId={selectedJobForTask.id}
              jobName={selectedJobForTask.name}
              onSaveTask={handleSaveTask}
              isLoading={isSavingTask}
              mode="create"
              fetchTasksForJob={fetchTasksForJob}
              refreshNotes={refreshNotes}
            />
          )}

          {editTaskModalOpen && selectedJobForTaskEdit && taskToEdit && (
            <TaskModal
              isOpen={editTaskModalOpen}
              onClose={() => {
                setEditTaskModalOpen(false);
                setTaskToEdit(null);
                setSelectedJobForTaskEdit(null);
              }}
              jobId={selectedJobForTaskEdit.id}
              jobName={selectedJobForTaskEdit.name}
              onSaveTask={handleTaskUpdate}
              isLoading={isUpdatingTask}
              taskToEdit={taskToEdit}
              mode="edit"
              fetchTasksForJob={fetchTasksForJob}
              refreshNotes={refreshNotes}
            />
          )}

          <SlideshowModal
            key={`${slideshowJobName}-${slideshowNotes[0]?.id || "no-notes"}`}
            isOpen={slideshowOpen}
            onClose={() => setSlideshowOpen(false)}
            notes={slideshowNotes}
            currentNoteIndex={slideshowStartIndex}
            jobName={slideshowJobName}
            showTaskName={true}
          />

          {jobPermissionModalOpen && selectedWorkspaceId && selectedUserId && (
            <Modal
              isOpen={jobPermissionModalOpen}
              onClose={() => {
                setJobPermissionModalOpen(false);
                setSelectedWorkspaceId(null);
                setSelectedUserId(null);
                setSelectedJobForPermission(null);
              }}
              title={`Manage Job Permissions - ${selectedJobForPermission?.jobName || "Job"}`}
            >
              <JobPermissionManagement
                defId={selectedWorkspaceId}
                users={[selectedUserId]}
                userId={selectedUserId}
                jobId={selectedJobForPermission?.jobId}
                defaultTab="userJobs"
              />
            </Modal>
          )}
        </div>
      </div>
    );
  });

StackedView.propTypes = {
  stackedJobs: PropTypes.array,
  loadingStackedJobs: PropTypes.bool,
  expandedStacks: PropTypes.object.isRequired,
  toggleStackExpansion: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool,
  searchTerm: PropTypes.string,
  openAiDialogForJob: PropTypes.func.isRequired,
  renderStackedImageIcon: PropTypes.func,
  displayNotes: PropTypes.array,
  selectedRow: PropTypes.any,
  handleRowClick: PropTypes.func,
  handleRowDoubleClick: PropTypes.func,
  handleAddFromRow: PropTypes.func,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleViewAttachments: PropTypes.func,
  handleReplyToNote: PropTypes.func,
  handlePriorityClick: PropTypes.func,
  handlePriorityMouseEnter: PropTypes.func,
  handlePriorityMouseLeave: PropTypes.func,
  handleNoteTextMouseEnter: PropTypes.func,
  handleNoteTextMouseLeave: PropTypes.func,
  handleLinkedNoteClick: PropTypes.func,
  handleLinkedNoteMouseEnter: PropTypes.func,
  handleLinkedNoteMouseLeave: PropTypes.func,
  isNoteReply: PropTypes.func,
  getReplyNoteId: PropTypes.func,
  isOriginalNoteExists: PropTypes.func,
  userStatusMap: PropTypes.object,
  loadingUsers: PropTypes.object,
  getPriorityValue: PropTypes.func,
  manuallyUpdatedPriorities: PropTypes.object,
  shouldShowNotePopup: PropTypes.func,
  isStackedViewLoading: PropTypes.bool,
  isFilteringStacked: PropTypes.bool,
  jobsToDisplay: PropTypes.array,
  onSaveTask: PropTypes.func,
  tasksByJob: PropTypes.object,
  loadingTasksByJob: PropTypes.object,
  expandedTasks: PropTypes.object,
  toggleTaskExpansion: PropTypes.func,
  notesByTask: PropTypes.object,
  loadingNotesByTask: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
  fetchNotesForTask: PropTypes.func,
  onTaskEdit: PropTypes.func,
  onTaskDelete: PropTypes.func,
  onTaskStatusChange: PropTypes.func,
  onTaskClick: PropTypes.func,
  refreshTasksForJob: PropTypes.func,
  onFilterByProject: PropTypes.func,
  onFilterByWorkspace: PropTypes.func,
  refreshNotes: PropTypes.func,
  highlightedTaskId: PropTypes.number,
  clearHighlightedTask: PropTypes.func,
  loadMoreTaskNotes: PropTypes.func,
  taskPagination: PropTypes.object,
  workspaceRole: PropTypes.number,
  // New props for lifted state
  expandedProjects: PropTypes.object,
  setExpandedProjects: PropTypes.func,
  activeTabByJob: PropTypes.object,
  handleTabChange: PropTypes.func,
  assigneeFilters: PropTypes.object,
  handleAssigneeFilter: PropTypes.func,
  clearAssigneeFilter: PropTypes.func,
};

StackedView.defaultProps = {
  stackedJobs: [],
  loadingStackedJobs: false,
  hasActiveFilters: false,
  searchTerm: "",
  renderStackedImageIcon: () => null,
  displayNotes: [],
  selectedRow: null,
  isStackedViewLoading: false,
  isFilteringStacked: false,
  jobsToDisplay: [],
  isOriginalNoteExists: () => false,
  onSaveTask: () => {},
  tasksByJob: {},
  loadingTasksByJob: {},
  expandedTasks: {},
  toggleTaskExpansion: () => {},
  notesByTask: {},
  loadingNotesByTask: {},
  fetchTasksForJob: () => {},
  fetchNotesForTask: () => {},
  onTaskEdit: () => {},
  onTaskDelete: () => {},
  onTaskStatusChange: () => {},
  onTaskClick: () => {},
  onFilterByProject: () => {},
  onFilterByWorkspace: () => {},
  refreshNotes: () => {},
  clearHighlightedTask: () => {},
  loadMoreTaskNotes: () => {},
  taskPagination: {},
  expandedProjects: {},
  setExpandedProjects: () => {},
  activeTabByJob: {},
  handleTabChange: () => {},
  assigneeFilters: {},
  handleAssigneeFilter: () => {},
  clearAssigneeFilter: () => {},
};

// Add CSS for responsive tabs and skeleton loading (dynamic injection – keep as is)
const responsiveCSS = `
/* Responsive tabs container */
.task-tabs-responsive {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
/* ... rest of your responsiveCSS ... */
`;

if (typeof document !== "undefined" && !document.getElementById("stacked-view-responsive-styles")) {
  const styleElement = document.createElement("style");
  styleElement.id = "stacked-view-responsive-styles";
  styleElement.textContent = responsiveCSS;
  document.head.appendChild(styleElement);
}

export default StackedView;